/**
 * One place that decides whether a visit counts as marketing traffic.
 *
 * Previously the first page_view was sent before anyone knew who the visitor
 * was, so staff working in the portal were counted as homeowner marketing
 * traffic. This module holds a single rule used by GA4, Clarity, GTM and the
 * Meta pixel alike:
 *
 *   marketing traffic = production website host
 *                     + a public (non-portal) page
 *                     + not a signed-in staff member
 *
 * A visitor whose sign-in state is not resolved yet is treated as an ordinary
 * anonymous homeowner, which is what nearly all of them are; as soon as a
 * staff role resolves, tracking stops for the rest of the browser session.
 * First-party analytics is unaffected and keeps recording everything.
 */

/** Path prefixes that are private working areas, never marketing pages. */
const NON_MARKETING_PREFIXES = [
  "/portal",
  "/admin",
  "/auth",
  "/login",
  "/signin",
  "/sign-in",
  "/signup",
  "/sign-up",
  "/register",
  "/reset-password",
  "/set-password",
  "/verify",
  "/installer",
  "/consultant",
  "/introducer",
  "/surveyor",
  "/customer",
  "/dashboard",
  "/preview",
];

/** Roles whose activity must never reach the marketing properties. */
const STAFF_ROLES = [
  "admin",
  "consultant",
  "installer",
  "partner",
  "marketing_manager",
  "introducer",
  "introducer_manager",
  "sales_manager",
  "customer_service",
  "surveyor",
];

const STAFF_FLAG_KEY = "evohome.marketing.staff_session";

let staffSession = false;

/** True when this path is a public marketing page. */
export function isMarketingPath(pathname: string | null | undefined): boolean {
  if (typeof pathname !== "string" || !pathname.startsWith("/")) return false;
  const path = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  return !NON_MARKETING_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/** True when any of these roles means "internal person, do not track". */
export function rolesAreStaff(roles: readonly (string | null | undefined)[]): boolean {
  return roles.some((role) => typeof role === "string" && STAFF_ROLES.includes(role));
}

/**
 * Record what the resolved sign-in tells us. Once a session is marked as
 * staff it stays marked, so a later signed-out render cannot re-enable
 * tracking for the same browsing session.
 */
export function markMarketingAudience(roles: readonly (string | null | undefined)[]): void {
  if (!rolesAreStaff(roles)) return;
  staffSession = true;
  try {
    window.sessionStorage?.setItem(STAFF_FLAG_KEY, "1");
  } catch {
    /* storage unavailable — the in-memory flag still holds for this page */
  }
}

/** True when this browsing session is known to belong to a staff member. */
export function isStaffSession(): boolean {
  if (staffSession) return true;
  try {
    if (window.sessionStorage?.getItem(STAFF_FLAG_KEY) === "1") {
      staffSession = true;
      return true;
    }
  } catch {
    /* storage unavailable */
  }
  return false;
}

/** Test seam: forget what we know about this session. */
export function resetMarketingAudience(): void {
  staffSession = false;
  try {
    window.sessionStorage?.removeItem(STAFF_FLAG_KEY);
  } catch {
    /* storage unavailable */
  }
}

/**
 * The single gate. `hostAllowed` comes from the production-host check so this
 * module stays free of GA4 imports (and the circular import that would cause).
 */
export function isMarketingAudience(hostAllowed: boolean, pathname?: string | null): boolean {
  if (typeof window === "undefined") return false;
  if (!hostAllowed) return false;
  if (isStaffSession()) return false;
  return isMarketingPath(pathname ?? window.location.pathname);
}
